import { Injectable } from '@angular/core';

import { TwcPopupOptions, TwcPopupService } from '@topcon/twc-angular';

import {
    ACL,
    AclControlService,
    AuthorizationService,
    helpUrl,
    MagnetService,
    TranslationService,
    UiConfigurationService,
} from '@mirage-projects/cw-commons';
import { ProjectType } from '@mirage-projects/cw-models';
import { CreateProjectService, CreateSiteService } from '@mirage-projects/cw-ui-components';

import { NotificationDrawerComponent } from '../components/notification-drawer/notification-drawer.component';
import { TemplateService, TemplatesTypes } from '../components/template-popup/template.service';
import { AboutDialogComponent } from '../components/topbar/about-dialog/about-dialog.component';

export interface OptionItemBar {
    isVisible?: boolean;
    isDivider?: boolean;
    keyText?: string;
    onClick?: () => void;
    code?: string;
}
export interface ItemBar {
    keyText: string;
    icon: string;
    isVisible: boolean;
    route: string | null;
    highlighted?: boolean;
    hideOnDesktop?: boolean;
    id: number;
    order: number;
    badge?: string;
    downloadUrl?: string;
    haveIcon?: boolean;
    haveText?: boolean;
    iconSize?: number;
    dropDown?: number;
    isPrimaryButton?: boolean;
    space?: boolean;
    isDivider?: boolean;
    showDivider?: boolean;
    options?: OptionItemBar[];
}
@Injectable({
    providedIn: 'root',
})
export class BarListConfigurationService {
    SIDEBAR_ITEMS: ItemBar[] = [];
    TOPBAR_ITEMS: ItemBar[] = [];

    private popupOptions: TwcPopupOptions = {
        closeByClickOutside: true,
    };
    constructor(
        private translationService: TranslationService,
        private createProjectService: CreateProjectService,
        private popupService: TwcPopupService,
        private uiConfigurationService: UiConfigurationService,
        private createSiteService: CreateSiteService,
        private magnetService: MagnetService,
        private aclControlService: AclControlService,
        private authorizationService: AuthorizationService,
        private templateService: TemplateService
    ) {
        if (!this.magnetService.isEmbed() && !this.magnetService.isSite()) {
            this.SIDEBAR_ITEMS = this.getSideBarItemsForCW();
        }

        if (!this.magnetService.isSite()) {
            this.TOPBAR_ITEMS = this.magnetService.isEmbed() ? this.getTopBarItemsForEmbed() : this.getTopBarItemsForCW();
        }
    }

    /**
     * Changes the app's language
     * @param pcvizCode pcviz language code
     */
    changeLanguage(pcvizCode: string): void {
        this.translationService.changeLanguage(pcvizCode);
    }

    getSideBarElements(): ItemBar[] {
        const combinedBars = [...this.TOPBAR_ITEMS, ...this.SIDEBAR_ITEMS];
        return combinedBars.sort((max, min) => max.order - min.order);
    }

    private openHelp(url?: helpUrl): void {
        window.open(this.uiConfigurationService.getHelpUrl(url), '_blank');
    }

    private getLanguageOptions(): OptionItemBar[] {
        const options: OptionItemBar[] = [];
        for (const language of this.translationService.LANGUAGES) {
            const opt = {
                keyText: language.localized,
                onClick: (): void => {
                    this.changeLanguage(language.pcvizCode);
                },
                code: language.pcvizCode,
                isVisible: true,
            };
            options.push(opt);
        }
        return options;
    }

    private getSideBarItemsForCW(): ItemBar[] {
        return [
            {
                keyText: 'DASHBOARD.DASHBOARD',
                id: 3,
                icon: 'dashboard',
                route: '/panel',
                order: 3,
                isVisible: true,
            },
            {
                keyText: 'NAVBAR.JOB_SITE',
                id: 11,
                icon: 'dozer',
                route: '/sites',
                order: 4,
                isVisible: !this.magnetService.isSite(),
            },
            {
                keyText: 'NAVBAR.PROJECTS',
                id: 4,
                icon: 'project',
                route: '/projects',
                order: 5,
                isVisible: true,
            },
            {
                keyText: 'NAVBAR.LAYERS',
                id: 5,
                icon: 'new-layer',
                route: '/layers',
                order: 6,
                isVisible: true,
            },
            {
                keyText: 'NAVBAR.USER_ADMIN',
                id: 6,
                icon: 'group',
                route: '/admin/authorization',
                order: 7,
                isVisible: this.aclControlService.hasUserACL(ACL.USERS_GET_LIST) || this.aclControlService.hasUserACL(ACL.GROUPS_GET_LIST),
            },
            {
                keyText: 'NAVBAR.REPORTS',
                id: 7,
                icon: 'report',
                route: '/reports',
                order: 8,
                isVisible: !this.magnetService.isSite(),
            },
            {
                keyText: 'NAVBAR.JOBS',
                icon: 'task',
                id: 8,
                route: this.magnetService.isSite() ? '/jobs' : '/process',
                order: 10,
                isVisible:
                    this.aclControlService.hasUserACL(ACL.TYPE_LAYERS_READ) &&
                    this.aclControlService.hasUserACL(ACL.TYPE_LAYERS_CREATE) &&
                    this.aclControlService.hasUserACL(ACL.TYPE_LAYERS_UPDATE),
            },
            {
                keyText: 'NAVBAR.EVENTS_LOG',
                icon: 'calendar',
                id: 9,
                route: '/events-log',
                order: 9,
                isVisible: this.authorizationService.isUserLoggedAdmin(),
            },
            {
                keyText: 'NAVBAR.SUPPORT',
                icon: 'gears',
                id: 10,
                route: '/support',
                highlighted: true,
                order: 11,
                isVisible: this.authorizationService.isSupportUser(),
            },
        ];
    }

    private getTopBarItemsForCW(): ItemBar[] {
        return [
            {
                keyText: 'NAVBAR.NEW',
                icon: 'layer-new',
                hideOnDesktop: true,
                id: 2,
                haveIcon: false,
                haveText: true,
                dropDown: 32,
                isPrimaryButton: true,
                route: '',
                order: 2,
                isVisible:
                    this.aclControlService.hasUserACL(ACL.TYPE_PROJECTS_CREATE) ||
                    this.aclControlService.hasUserACL(ACL.TYPE_LAYERS_CREATE) ||
                    this.aclControlService.hasUserACL(ACL.TYPE_SITES_CREATE),
                options: [
                    {
                        keyText: 'NAVBAR.NEW_PROJECT',
                        onClick: (): void => {
                            this.createProjectService.openProjectDialog(ProjectType.COLLAGE_TYPE);
                        },
                        isVisible: this.aclControlService.hasUserACL(ACL.TYPE_PROJECTS_CREATE),
                    },
                    {
                        keyText: 'NAVBAR.NEW_SCAN_PROJECT',
                        onClick: (): void => {
                            this.createProjectService.openProjectDialog(ProjectType.SCAN);
                        },
                        isVisible: this.aclControlService.hasUserACL(ACL.TYPE_PROJECTS_CREATE),
                    },
                    {
                        keyText: 'NAVBAR.NEW_PROJECT_FROM_ZIP',
                        onClick: (): void => {
                            this.createProjectService.openWizard({ importType: 'project', selectFirstJobAdapter: true });
                        },
                        isVisible: this.aclControlService.hasUserACL(ACL.TYPE_PROJECTS_CREATE),
                    },
                    {
                        keyText: 'NAVBAR.NEW_LAYER',
                        onClick: (): void => {
                            this.createProjectService.openWizard({ importType: 'layer' });
                        },
                        isVisible: this.aclControlService.hasUserACL(ACL.TYPE_LAYERS_CREATE),
                    },
                    {
                        keyText: 'NAVBAR.NEW_JOB_SITE',
                        onClick: (): void => {
                            this.createSiteService.openSiteDialog();
                        },
                        isVisible: this.aclControlService.hasUserACL(ACL.TYPE_SITES_CREATE),
                    },
                ],
            },
            {
                keyText: 'NAVBAR.SETTINGS',
                icon: 'settings',
                hideOnDesktop: true,
                id: 1,
                order: 1,
                haveIcon: true,
                haveText: false,
                iconSize: 30,
                dropDown: 30,
                space: true,
                isPrimaryButton: false,
                route: '',
                showDivider: true,
                isVisible: true,
                options: [
                    {
                        keyText: 'NOTIFICATION_PANEL.TITLE',
                        onClick: (): void => {
                            this.popupService.open(NotificationDrawerComponent, {});
                        },
                        isVisible: true,
                    },
                    {
                        isDivider: true,
                        isVisible: true,
                    },
                    {
                        keyText: 'NAVBAR.ABOUT',
                        onClick: (): void => {
                            this.popupService.open(AboutDialogComponent, this.popupOptions);
                        },
                        isVisible: true,
                    },
                ],
            },
            {
                keyText: 'NAVBAR.LANGUAGE',
                icon: 'flag-mirage',
                hideOnDesktop: true,
                id: 12,
                order: 12,
                haveIcon: true,
                haveText: false,
                iconSize: 24,
                dropDown: 30,
                isPrimaryButton: false,
                route: '',
                options: this.getLanguageOptions(),
                isVisible: true,
            },
            {
                keyText: 'NAVBAR.HELP',
                icon: 'help',
                hideOnDesktop: true,
                id: 12,
                order: 13,
                haveIcon: true,
                haveText: false,
                iconSize: 30,
                dropDown: 30,
                isPrimaryButton: false,
                route: '',
                isVisible: true,
                options: [
                    {
                        keyText: 'NAVBAR.HELP',
                        onClick: (): void => {
                            this.openHelp();
                        },
                        isVisible: true,
                    },
                    {
                        keyText: 'NAVBAR.SCAN_HELP',
                        onClick: (): void => {
                            this.openHelp(helpUrl.SCAN);
                        },
                        isVisible: true,
                    },
                    {
                        keyText: 'NAVBAR.SUPPORT_HELP',
                        onClick: (): void => {
                            this.openHelp(helpUrl.SUPPORT);
                        },
                        isVisible: true,
                    },
                ],
            },
            {
                keyText: 'NAVBAR.CLOUD_CONNECTOR',
                icon: 'cloud-connector',
                hideOnDesktop: true,
                id: 13,
                order: 14,
                haveIcon: true,
                haveText: false,
                iconSize: 30,
                isPrimaryButton: false,
                route: '',
                isVisible: !this.magnetService.isSite(),
                downloadUrl: this.magnetService.CLOUD_CONNECTOR_URL,
            },
        ];
    }

    private getTopBarItemsForEmbed(): ItemBar[] {
        return [
            {
                keyText: 'NAVBAR.DOCUMENTS',
                icon: 'document',
                hideOnDesktop: false,
                id: 1,
                order: 1,
                haveIcon: true,
                haveText: false,
                iconSize: 30,
                dropDown: 30,
                isPrimaryButton: false,
                route: '',
                isVisible: true,
                options: [
                    {
                        keyText: 'FOOTER.TERMS',
                        onClick: (): void => {
                            this.templateService.showTemplate(TemplatesTypes.LEGAL);
                        },
                        isVisible: true,
                    },
                    {
                        keyText: 'FOOTER.PRIVACY_STATEMENT',
                        onClick: (): void => {
                            this.templateService.showTemplate(TemplatesTypes.PRIVACY);
                        },
                        isVisible: true,
                    },
                    {
                        keyText: 'NAVBAR.HELP',
                        onClick: (): void => {
                            this.openHelp();
                        },
                        isVisible: true,
                    },
                ],
            },
        ];
    }
}
